import { useState, useEffect } from 'react';
// @mui
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  Stack,
  Link,
  TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
// components
import Iconify from '../../components/Iconify';
import Label from '../../components/Label';
import SvgIconStyle from '../../components/SvgIconStyle';
import Image from '../../components/Image';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
// utils
import axios from '../../utils/axios';

// ----------------------------------------------------------------------

const DIOText = styled('span')(({ theme }) => ({
  fontFamily: theme.typography.subFontFamily,
}));


export default function ApplyFreePlanDialog({ open, closeDialog }) {
  const smUp = useResponsive('up', 'sm');
  const { user, getUser } = useAuth();
  const [step, setStep] = useState(0);
  const [story, setStory] = useState('');
  const [isSubmit, setIsSubmit] = useState(false);

  const formatPhone = (phone) => {
    const cleaned = ('' + phone).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{4})(\d{4})$/);
    if (match) {
      return match[1] + '-' + match[2] + '-' + match[3];
    }
    return phone;
  }

  useEffect(() => {
    if (open) {
      setStep(0);
      setStory('');
      setIsSubmit(false);
    }
  }, [open]);

  const sendStory = async () => {
    try {
      setIsSubmit(true);
      const companyId = user?.employee.company?.[0]?.id;
      await axios.post(`/api/company/${companyId}/scout/apply/story/`, { story });
      setStep(2);
      setIsSubmit(false);
    } catch (err) {
      setIsSubmit(false);
      console.error(err);
    }
  };

  return (
    <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="dg" fullScreen={!smUp && step === 1}>
      {step === 0 && (
      <DialogContent sx={{ p: { xs: 3, sm: 4 }, pb: "8px!important" }}>
        <Typography variant="h5">
          미리 확보해 두고, 필요할 때 바로 협업을 시작하세요.
        </Typography>
        {smUp ? (
          <Image
            alt="dio-scout-apply"
            src="https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/scout/scout-apply-free.png"
            srcSet="
              https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/scout/scout-apply-free.png 1x,
              https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/scout/scout-apply-free-2x.png 2x,
            "
            sx={{
              my: 3,
              width: '100%',
            }}
          />
        ) : (
          <Image
            alt="dio-scout-apply-mo"
            src="https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/scout/scout-apply-free-mo.png"
            srcSet="
              https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/scout/scout-apply-free-mo.png 1x,
              https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/scout/scout-apply-free-mo-2x.png 2x,
            "
            sx={{
              my: 3,
              width: '100%',
            }}
          />
        )}

        <Stack direction="row" spacing={1} alignItems={"flex-start"} sx={{ mb: 1.5 }}>
          <SvgIconStyle src="/icons/icon-crown-mono.svg" sx={{ width: 20, height: 20, color: "text.disabled" }} />
          <Typography variant="subtitle1">
            <DIOText>DIO</DIOText> SCOUT : 검증된 프리랜서를 미리 준비해두세요.
          </Typography>
        </Stack>
        <ul style={{ marginLeft: "16px", marginBottom: "8px" }}>
          <li>
            <Typography variant="body2">
              공고 없이도 적합한 프리랜서를 추천받고, 미리 네트워크를 형성할 수 있어요.
            </Typography>
          </li>
          <li>
            <Stack direction="row" alignItems="center" sx={{ display: "flex", flexWrap: "wrap", gap: 1, rowGap: 0 }}>
              <Typography variant="body2">
                즉시 파트타이머가 필요한 상황이라면,
              </Typography>
              <Stack direction="row" alignItems="center">
                <SvgIconStyle src="/icons/icon-link-mono.svg" sx={{ width: "16px", height: "16px", color: "text.primary" }} />
                <Link variant="subtitle2" underline="always" href="/jobs/">
                  <DIOText>DIO</DIOText> 파트타임 채용하기
                </Link>
              </Stack>
            </Stack>
          </li>
        </ul>
      </DialogContent>
      )}
      {step === 1 && (
      <DialogContent sx={{ p: { xs: 3, sm: 4 }, pb: "8px!important", ...!smUp && { mt: "16px!important" } }}>
        <Typography variant="h5" sx={{ wordBreak: "break-word" }} gutterBottom>
          회사에서 찾고 있는 포지션을 구체적으로 알려주세요.
        </Typography>
        <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all" }}>
          채용 공고가 있다면 링크만 남겨주셔도 됩니다.
        </Typography>
        <TextField
          sx={{ mt: 3, mb: 2}}
          value={story}
          onChange={(e) => setStory(e.target.value)}
          fullWidth
          multiline
          rows={smUp ? 5 : 10}
        />

        <Box
          sx={{ bgcolor: "grey.200", borderRadius: 1.5, p: 2 }}
        >
          <Stack direction="row" spacing={1} alignItems={"center"} sx={{ mb: 1 }}>
            <Label color="info" variant="filled">
              예시
            </Label>
            <Typography variant="subtitle2">
              자세한 조건을 모두 작성해주셔도 됩니다.
            </Typography>
          </Stack>
          <ul style={{ marginLeft: "16px", marginBottom: "8px" }}>
            <li>
              <Typography variant="caption">
                헬스케어 IoT 디바이스 연동 경험이 있는 4-8년차 Java 백엔드 개발자
              </Typography>
            </li>
            <li>
              <Typography variant="caption">
                남미에 중장비 수출 추진 경험이 있는 6년차 이상 세일즈 매니저
              </Typography>
            </li>
            <li>
              <Typography variant="caption">
                마케팅 데이터 경험이 있는 8년차 이상 데이터 사이언티스트
              </Typography>
            </li>
          </ul>
        </Box>
      </DialogContent>
      )}
      {step === 2 && (
      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Iconify icon={"eva:info-fill"} sx={{ width: 24, height: 24, color: 'info.main' }} />
        <Typography variant="h5" gutterBottom>
          <DIOText /> SCOUT 안내를 위해 연락드릴게요.
        </Typography>
        <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all" }}>
          • 연락 받으실 전화번호 : <b>{formatPhone(user?.phone)}</b>
        </Typography>
        <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all" }}>
          • 영업일 3일 이내에 연락드리겠습니다.
        </Typography>
      </DialogContent>
      )}
      {step === 0 && (
      <DialogActions sx={{ m: { xs: 0, sm: 1 } }}>
        <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1} sx={{ width: 1 }}
          justifyContent="flex-end"
        >
          <Button
            variant="text"
            onClick={closeDialog}
            size="large"
            sx={{ px: 4 }}
            fullWidth={!smUp}
          >
            인재 추천 받지 않기
          </Button>
          <Button
            variant="contained"
            onClick={() => setStep(1)}
            size="large"
            sx={{ px: 4 }}
            fullWidth={!smUp}
          >
            인재 추천 받기
          </Button>
        </Stack>
      </DialogActions>
      )}
      {step === 1 && (
      <DialogActions sx={{ m: { xs: 0, sm: 1 } }}>
        <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1} sx={{ width: 1 }}
          justifyContent="flex-end"
        >
          <LoadingButton
            variant="text"
            onClick={closeDialog}
            size="large"
            sx={{ px: 4 }}
            fullWidth={!smUp}
            loading={isSubmit}
          >
            취소
          </LoadingButton>
          <LoadingButton
            variant="contained"
            onClick={sendStory}
            size="large"
            sx={{ px: 4 }}
            fullWidth={!smUp}
            loading={isSubmit}
            disabled={story.trim().length === 0}
          >
            완료
          </LoadingButton>
        </Stack>
      </DialogActions>
      )}
      {step === 2 && (
      <DialogActions sx={{ m: { xs: 0, sm: 1 } }}>
        <Button
          variant="contained"
          onClick={() => {getUser(); closeDialog();}}
          size="large"
          sx={{ px: 4 }}
          fullWidth={!smUp}
        >
          확인했어요
        </Button>
      </DialogActions>
      )}
    </Dialog>
  )
}